"use client";

import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";

export type SlotProps = React.HTMLAttributes<HTMLElement> & {
  children?: React.ReactNode;
};

function composeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined | null>
): React.RefCallback<T> {
  return (node) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
}

const REACT_LAZY_TYPE = Symbol.for("react.lazy");

type LazyNode = {
  $$typeof: symbol;
  _payload: unknown;
  _init: (payload: unknown) => React.ReactNode;
};

function asLazyNode(node: React.ReactNode): LazyNode | null {
  if (typeof node !== "object" || node === null) return null;
  const candidate = node as Partial<LazyNode>;
  return candidate.$$typeof === REACT_LAZY_TYPE &&
    typeof candidate._init === "function"
    ? (candidate as LazyNode)
    : null;
}

/**
 * 🔴 **서버 컴포넌트가 넘긴 자식은 element가 아니라 lazy로 도착한다.**
 *
 * `Slot`은 클라이언트 컴포넌트이므로, 서버 컴포넌트(예: `asChild`를 쓴 RSC 페이지)가
 * `<Button asChild><Link/></Button>`을 그리면 그 `<Link/>`는 RSC 경계를 넘으며
 * `Symbol(react.lazy)`로 감싸인다. 그대로 `isValidElement`에 물으면 **false**라
 * Slot이 `null`을 반환하고, **서버 HTML에서 그 버튼이 통째로 사라진다**. 클라이언트에서는
 * lazy가 이미 풀려 있어 정상 렌더되므로 결과는 hydration 불일치다(실측 2026-08-16:
 * 브랜드프론트 프리뷰의 닫기 버튼이 서버 HTML에 `<a>` 0개, 클라이언트에만 존재).
 *
 * 그래서 판정 전에 한 겹 푼다. `_init`은 React가 lazy를 렌더할 때 부르는 것과 같은
 * 함수이며, 아직 도착하지 않았으면 promise를 throw해 가장 가까운 Suspense가 받는다
 * (SSR 시점에는 이미 도착해 있는 것이 보통이다).
 *
 * ⚠️ **`isValidElement`가 false여도 조용히 `null`을 반환하는 성질은 유지한다** —
 * 텍스트·숫자·배열을 자식으로 준 잘못된 호출을 감싸 렌더하면 병합한 className·ref가
 * 갈 곳이 없어 더 어긋난다. 고칠 것은 "element인데 못 알아본 경우" 하나다.
 */
function unwrapLazy(node: React.ReactNode): React.ReactNode {
  const lazy = asLazyNode(node);
  return lazy ? lazy._init(lazy._payload) : node;
}

export const Slot = React.forwardRef<HTMLElement, SlotProps>(function Slot(
  { children, ...slotProps },
  forwardedRef,
) {
  const resolved = unwrapLazy(children);
  if (!React.isValidElement(resolved)) {
    return null;
  }
  const child = resolved as React.ReactElement<Record<string, unknown>>;
  const childRef = (child as React.ReactElement<{ ref?: React.Ref<unknown> }>)
    .props.ref as React.Ref<HTMLElement> | undefined;
  const merged = mergeProps(
    slotProps as Record<string, unknown>,
    child.props as Record<string, unknown>,
  );
  return React.cloneElement(child, {
    ...merged,
    ref: composeRefs<HTMLElement>(forwardedRef, childRef),
  });
});
